import axios from 'axios';

// ---------------------------------------------------------------------------
// SESSÃO (item 7, 23/09/2026) — renovação de token e inatividade, no navegador.
//
// Regras (cartilha Zendesk/Freshdesk/Intercom, decididas com o mantenedor):
//   • token de acesso curto (30 min); sessão até 24 h; inatividade 1 h — as três valem no SERVIDOR (users/refresh-token);
//   • aqui: (1) registrar a última INTERAÇÃO HUMANA (clique, tecla, rolagem, toque) — chamadas de fundo (SignalR, contadores)
//     NÃO contam; (2) renovar sozinho perto do vencimento, só se houve interação recente; (3) em 401 por token vencido,
//     renovar UMA vez, enfileirar o que chegar durante a renovação e repetir tudo com o token novo; (4) se a renovação for
//     recusada (revogada / inativa / vencida), encerrar com o motivo na tela de login.
//   • TOKEN RECUSADO COM HTTP 200 (02/10/2026): quando o token morre na VALIDAÇÃO (vencido, sessão/usuário derrubado), as APIs
//     respondem 200 com { success:false, errors:["Error: invalid_token"] } (OnChallenge antigo, sem status). O item (3) só olhava
//     401, então esse caso passava direto: a tela mostrava "token inválido" e só voltava a funcionar quando o batimento renovava
//     (até 30 s depois). Agora os dois formatos (401 e 200 com invalid_token) renovam e repetem do mesmo jeito.
//   • refresh token: opaco, uso único, rotativo — guardado em localStorage ('refreshtoken'); o servidor só tem o hash.
//   • os 3 tokens (2DO, Reports, 2Talk) são renovados juntos: cada instância do axios registra-se com attachSession(instance, chave).
//   • ABAS PARALELAS: o localStorage é um só; só uma aba renova por vez (navigator.locks; sem ele, a mesma promessa da aba);
//     quem esperou a trava e viu o refresh mudar não chama o servidor, só aplica os tokens novos; recusa "session_invalid" com
//     refresh já trocado por outra aba = usa o novo em vez de encerrar; o evento 'storage' propaga os tokens para as outras abas.
// Sem async/await em arrow class field (não há classe aqui; funções normais e Promise chain).
// ---------------------------------------------------------------------------

const KEY_REFRESH = 'refreshtoken';
const KEY_ACTIVITY = 'mexx.session.lastActivity';
const KEY_TOKENS = { accesstoken: 'accesstoken', accesstokenreports: 'accesstokenreports', accesstoken2talk: 'accesstoken2talk' };
const LOCK_NAME = 'mexx-session-refresh';
const ACTIVITY_THROTTLE_MS = 10 * 1000;       // grava no máximo a cada 10 s
const KEY_LAST_REFRESH = 'mexx.session.lastRefresh';
const KEY_IDLE_MINUTES = 'mexx.session.idleMinutes'; // inatividade em vigor, vinda do login/renovação (sessionidleminutes)
const KEY_ACTIVITY_SOURCE = 'mexx.session.lastActivitySource'; // diagnóstico: QUAL evento marcou a última interação ("scroll@DIV.x 19:46:01")
const DEFAULT_IDLE_MIN = 60;
const CHECK_EVERY_MS = 30 * 1000;
const REASONS = ['session_inactive', 'session_revoked', 'session_expired', 'session_invalid'];

// Janelas do item 5, CALIBRADAS pela inatividade configurada (tb_config.session_idle_minutes, que a API manda no login/renovação):
//   • batimento = inatividade/3 (teto 10 min): renova enquanto há interação, para a "última renovação" no servidor nunca ficar a
//     mais de 1/3 do prazo da última interação real. Com 60 min: a cada 10 min; com 5 min: a cada ~100 s.
//   • "gente na frente" = interação dentro de inatividade/2 (teto 5 min). Com 60: 5 min; com 5: 2,5 min.
//   • perto de vencer = 1/3 da vida do token (teto 5 min), calculado pelo exp do próprio token.
// Sem o valor (front novo com API antiga) vale 60 min = as constantes de antes (10 / 5 / 5).
function idleMs() { const m = Number(safeGet(KEY_IDLE_MINUTES)); return (m > 0 ? m : DEFAULT_IDLE_MIN) * 60 * 1000; }
function heartbeatMs() { return Math.min(10 * 60 * 1000, Math.floor(idleMs() / 3)); }
function recentActivityMs() { return Math.min(5 * 60 * 1000, Math.floor(idleMs() / 2)); }
function renewBeforeMs(tokenLifeMs) { return Math.min(5 * 60 * 1000, tokenLifeMs > 0 ? Math.floor(tokenLifeMs / 3) : 5 * 60 * 1000); }
const instances = []; // { instance, tokenKey }
let refreshing = null; // Promise em andamento (uma renovação por vez nesta aba)
let lastActivityWrite = 0;
let installed = false;

function safeGet(k) { try { return localStorage.getItem(k); } catch (e) { return null; } }
function safeSet(k, v) { try { localStorage.setItem(k, v); } catch (e) { /* modo privado / cota */ } }
function safeRemove(k) { try { localStorage.removeItem(k); } catch (e) { /* idem */ } }

// exp do JWT (segundos de época) sem biblioteca: só decodifica o payload; não valida assinatura (isso é do servidor).
export function tokenExpiry(token) {
  try {
    const part = String(token || '').split('.')[1];
    if (!part) return null;
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(part.length / 4) * 4, '='));
    const exp = JSON.parse(json).exp;
    return exp ? exp * 1000 : null;
  } catch (e) { return null; }
}

// ev = o evento do navegador (ou { type } sintético: login, init, visibilitychange). Além da marca de tempo, grava QUAL evento foi
// e de que elemento veio (diagnóstico de 03/10/2026: sessão parada que renovava sozinha). Para ver: no console,
// localStorage.getItem('mexx.session.lastActivitySource'); com window.__mexxSessionDebug = true cada interação sai no console.
export function touchActivity(ev) {
  const now = Date.now();
  if (now - lastActivityWrite < ACTIVITY_THROTTLE_MS) return;
  lastActivityWrite = now;
  safeSet(KEY_ACTIVITY, String(now));
  try {
    const type = ev && ev.type ? ev.type : 'app';
    const t = ev && ev.target;
    let target = 'window';
    if (t === document) target = 'document';
    else if (t && t !== window && t.tagName) target = `${t.tagName}${t.id ? `#${t.id}` : ''}${typeof t.className === 'string' && t.className ? `.${t.className.split(' ')[0]}` : ''}`;
    safeSet(KEY_ACTIVITY_SOURCE, `${type}@${target} ${new Date(now).toLocaleTimeString()}`);
    if (typeof window !== 'undefined' && window.__mexxSessionDebug) console.debug('[sessao] interacao', type, target); // eslint-disable-line no-console
  } catch (e) { /* diagnóstico nunca atrapalha */ }
}

export function lastActivity() { return Number(safeGet(KEY_ACTIVITY)) || 0; }
export function recentlyActive() { return Date.now() - lastActivity() < recentActivityMs(); }
export function hasRefreshToken() { return !!safeGet(KEY_REFRESH); }

// Header Authorization de cada instância registrada = token que está no localStorage (desta aba ou de outra).
function applyStoredHeaders() {
  instances.forEach(({ instance, tokenKey }) => {
    const t = safeGet(tokenKey);
    if (t && instance && instance.defaults && instance.defaults.headers) instance.defaults.headers.common['Authorization'] = 'Bearer ' + t;
  });
}

// Guarda os 3 tokens + refresh e atualiza o header de cada instância registrada. NÃO marca interação humana.
function saveTokens(data) {
  if (!data) return;
  if (data.accesstoken) safeSet(KEY_TOKENS.accesstoken, data.accesstoken);
  if (data.accesstokenreports) safeSet(KEY_TOKENS.accesstokenreports, data.accesstokenreports);
  if (data.accesstoken2talk) safeSet(KEY_TOKENS.accesstoken2talk, data.accesstoken2talk);
  if (data.refreshtoken) safeSet(KEY_REFRESH, data.refreshtoken);
  if (Number(data.sessionidleminutes) > 0) safeSet(KEY_IDLE_MINUTES, String(Number(data.sessionidleminutes)));
  safeSet(KEY_LAST_REFRESH, String(Date.now()));
  applyStoredHeaders();
}

// Chamado pelo LOGIN: guarda os tokens e marca a interação (a pessoa acabou de entrar).
// A RENOVAÇÃO usa saveTokens, sem marcar interação. Antes (até 03/10/2026) a renovação também chamava touchActivity: cada renovação
// virava "interação recente", o batimento seguinte encontrava gente "na frente" e renovava de novo, para sempre. Com inatividade
// abaixo de 15 min (batimento = inatividade/3 menor que a janela de interação = inatividade/2) a aba aberta e parada nunca caía:
// o prazo de inatividade voltava ao máximo a cada batimento (medido em DEMO com 6 min: 17 renovações em 33 min sem ninguém mexer).
export function storeTokens(data) {
  if (!data) return;
  saveTokens(data);
  touchActivity({ type: 'login' });
}

export function clearSession() {
  safeRemove(KEY_REFRESH);
  safeRemove(KEY_ACTIVITY);
  safeRemove(KEY_LAST_REFRESH);
  safeRemove(KEY_IDLE_MINUTES);
  safeRemove(KEY_ACTIVITY_SOURCE);
}

// Encerra no navegador e manda para o login com o motivo (a tela de login mostra a mensagem).
function endSession(reason) {
  clearSession();
  try { localStorage.clear(); } catch (e) { /* ignora */ }
  const why = REASONS.indexOf(reason) >= 0 ? reason : 'session_expired';
  document.location.href = '/?session=' + why;
}

// Trava entre abas (Web Locks). Onde não existir, corre sem trava: a promessa única já protege dentro da aba.
function withCrossTabLock(fn) {
  try {
    if (typeof navigator !== 'undefined' && navigator.locks && navigator.locks.request) return navigator.locks.request(LOCK_NAME, fn);
  } catch (e) { /* cai no sem trava */ }
  return fn();
}

function errorReason(err) {
  const msg = (err && err.response && err.response.data && err.response.data.errors && err.response.data.errors[0]) || (err && err.message) || '';
  return REASONS.indexOf(msg) >= 0 ? msg : null;
}

// Uma tentativa de POST users/refresh-token com o refresh atual do localStorage. Resolve com os tokens novos.
function callRefresh(refresh, baseURL) {
  // Nos dois formatos: a 2DO desserializa com resolver de minúsculas; assim o campo casa em qualquer configuração.
  return axios.post(baseURL + '/users/refresh-token', { refreshToken: refresh, refreshtoken: refresh }, { headers: { 'Content-Type': 'application/json' } })
    .then((res) => {
      const body = res && res.data;
      if (!body || body.success === false || !body.data || !body.data.accesstoken) {
        const e = new Error((body && body.errors && body.errors[0]) || 'session_invalid');
        e.sessionReason = errorReason(e) || 'session_invalid';
        throw e;
      }
      saveTokens(body.data); // renovação não é interação humana (ver storeTokens)
      return body.data;
    });
}

// Renovação: uma por vez nesta aba e, com Web Locks, uma por vez entre abas. Nunca encerra sem motivo do servidor.
export function refreshSession() {
  if (refreshing) return refreshing;
  const baseURL = safeGet('urlapi');
  const before = safeGet(KEY_REFRESH);
  if (!before || !baseURL) return Promise.reject(new Error('session_invalid'));
  refreshing = withCrossTabLock(() => {
    // Outra aba renovou enquanto esta esperava a trava: os tokens novos já estão no localStorage. Não chama o servidor.
    const current = safeGet(KEY_REFRESH);
    if (!current) { const e = new Error('session_invalid'); e.sessionReason = 'session_invalid'; throw e; }
    if (current !== before) { applyStoredHeaders(); return Promise.resolve({ fromOtherTab: true }); }
    return callRefresh(current, baseURL).catch((err) => {
      // Recusa "session_invalid" com o refresh JÁ TROCADO por outra aba (sem Web Locks): usa o novo em vez de encerrar.
      const reason = err && err.sessionReason ? err.sessionReason : errorReason(err);
      const now = safeGet(KEY_REFRESH);
      if (reason === 'session_invalid' && now && now !== current) { applyStoredHeaders(); return { fromOtherTab: true }; }
      const e = new Error(reason || (err && err.message) || 'session_invalid');
      // Sem resposta do servidor (rede/servidor fora): NÃO encerra — deixa o pedido original falhar e o usuário tentar de novo.
      e.sessionReason = err && !err.response && !err.sessionReason ? null : (reason || 'session_invalid');
      throw e;
    });
  }).finally(() => { refreshing = null; });
  return refreshing;
}

function isAuthFailure(error) {
  const st = error && error.response && error.response.status;
  return st === 401;
}

// Token recusado que NÃO pede renovação (03/10/2026): (1) o pedido saiu com um token diferente do guardado; (2) os tokens foram guardados há
// menos de 10 s (login ou renovação recém-feita) — o pedido quase sempre saiu antes da troca, e renovar AGORA é o que derrubava o teste
// em DEMO: a renovação disparada 2 s depois do login tinha a resposta perdida no recarregamento da página; o batimento seguinte
// apresentava o refresh antigo e o servidor encerrava a sessão por reuso (refresh_reuse).
function shouldRetryWithoutRenewing(cfg, tokenKey) {
  if (sentWithStaleToken(cfg, tokenKey)) return true;
  const lastStored = Number(safeGet(KEY_LAST_REFRESH)) || 0;
  return lastStored > 0 && Date.now() - lastStored < JUST_STORED_MS;
}

// Resposta 200 cujo corpo diz que o TOKEN foi recusado na validação: { success:false, errors:["Error: invalid_token"] }.
// Só "invalid_token": "Error: Unauthorized" (sem token) e erros de negócio não renovam nada.
function isInvalidTokenBody(data) {
  try {
    let body = data;
    if (typeof body === 'string') {
      if (body.indexOf('invalid_token') < 0) return false;
      body = JSON.parse(body);
    }
    if (!body || body.success !== false || !body.errors || !body.errors.length) return false;
    return String(body.errors[0] || '').indexOf('invalid_token') >= 0;
  } catch (e) { return false; }
}

// Pedidos que nunca disparam renovação (a própria renovação, o login e a limpeza de claims do logout).
function isSessionExempt(cfg) {
  const url = String((cfg && cfg.url) || '');
  return url.indexOf('refresh-token') >= 0 || url.indexOf('/login') >= 0 || url.indexOf('removeClaims') >= 0;
}

const JUST_STORED_MS = 10 * 1000; // tokens guardados há menos que isto = login/renovação acabou de acontecer

// O pedido que falhou foi enviado com um token DIFERENTE do que está guardado (cabeçalho antigo de uma instância criada antes do login,
// pedido disparado durante a troca de tokens): não é o token atual que foi recusado. Repetir com o atual basta; renovar seria errado.
function sentWithStaleToken(cfg, tokenKey) {
  try {
    const current = safeGet(tokenKey);
    const h = (cfg && cfg.headers) || {};
    const auth = h.Authorization || h.authorization || (h.common && h.common.Authorization);
    const sent = typeof auth === 'string' && auth.indexOf('Bearer ') === 0 ? auth.slice(7) : null;
    return !!(current && sent && sent !== current);
  } catch (e) { return false; }
}

// Repete o pedido com o token guardado, sem renovar.
function retryWithCurrent(instance, tokenKey, cfg) {
  cfg.__sessionRetried = true;
  const t = safeGet(tokenKey);
  cfg.headers = Object.assign({}, cfg.headers || {}, { Authorization: 'Bearer ' + t });
  return instance.request(cfg);
}

// Renova UMA vez e repete o pedido com o token novo. giveUp() = o que devolver quando não deu para renovar sem motivo do servidor
// (rede fora): o resultado original, para a tela tratar como sempre tratou. Com motivo do servidor: encerra na tela de login.
// SEM GENTE NA FRENTE NÃO RENOVA (03/10/2026): o servidor mede a inatividade pela última renovação. Se um pedido de fundo (contador,
// lista que se atualiza sozinha) renovasse ao receber token vencido, a sessão de uma aba abandonada nunca cairia por inatividade.
// Sem interação recente, o 401 segue para a tela como está: o tratamento de token inválido leva ao login, que é o resultado
// certo para quem passou do prazo de inatividade.
function renewAndRetry(instance, tokenKey, cfg, giveUp) {
  cfg.__sessionRetried = true;
  if (!recentlyActive()) return giveUp();
  return refreshSession()
    .then(() => {
      const t = safeGet(tokenKey);
      cfg.headers = Object.assign({}, cfg.headers || {}, { Authorization: 'Bearer ' + t });
      return instance.request(cfg);
    })
    .catch((e) => {
      if (e && e.sessionReason) { endSession(e.sessionReason); return new Promise(() => {}); } // página vai trocar; segura a promessa
      return giveUp();
    });
}

// Instala o interceptor de token recusado e o header inicial numa instância do axios. tokenKey = chave do localStorage do token dela.
export function attachSession(instance, tokenKey) {
  if (!instance || !instance.interceptors || instance.__sessionAttached) return instance;
  instance.__sessionAttached = true;
  instances.push({ instance, tokenKey });
  instance.interceptors.response.use(
    (response) => {
      // HTTP 200 com invalid_token no corpo (token recusado na validação): mesmo tratamento do 401.
      const cfg = response && response.config;
      if (!cfg || cfg.__sessionRetried || !isInvalidTokenBody(response.data)) return response;
      if (isSessionExempt(cfg) || !hasRefreshToken()) return response;
      if (shouldRetryWithoutRenewing(cfg, tokenKey)) return retryWithCurrent(instance, tokenKey, cfg);
      return renewAndRetry(instance, tokenKey, cfg, () => response);
    },
    (error) => {
      const cfg = error && error.config;
      if (!cfg || !isAuthFailure(error) || cfg.__sessionRetried) return Promise.reject(error);
      if (isSessionExempt(cfg) || !hasRefreshToken()) return Promise.reject(error);
      if (shouldRetryWithoutRenewing(cfg, tokenKey)) return retryWithCurrent(instance, tokenKey, cfg);
      return renewAndRetry(instance, tokenKey, cfg, () => Promise.reject(error));
    },
  );
  installActivityTracking();
  return instance;
}

// Ouvintes de interação humana, propagação entre abas e verificação periódica do vencimento. Uma vez por página.
function installActivityTracking() {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  // O que conta como gente na frente (03/10/2026): tecla, ponteiro/clique, toque e RODA do mouse. Movimento do mouse NÃO conta
  // (esbarrar no mouse não é usar o sistema, e o evento dispara dezenas de vezes por segundo).
  // 'scroll' SAIU da lista: o evento de rolagem também dispara quando o PRÓPRIO sistema rola a página (lista que se atualiza e
  // encolhe, "rolar até" de um componente, chat que avança sozinho). Era o único item da lista que o código consegue disparar
  // sem ninguém mexer, e sessão parada continuava "ativa" (medido em DEMO: renovação a cada 2,5 min com a pessoa longe).
  // 'wheel' e 'touchmove' são a rolagem feita por uma pessoa (quem lê uma tela longa rola).
  ['keydown', 'pointerdown', 'mousedown', 'touchstart', 'touchmove', 'wheel'].forEach((ev) => window.addEventListener(ev, touchActivity, { passive: true }));
  document.addEventListener('visibilitychange', () => { if (!document.hidden) touchActivity({ type: 'visibilitychange', target: document }); });
  // Outra aba renovou (ou saiu): esta aba passa a usar os tokens novos sem chamar o servidor.
  window.addEventListener('storage', (ev) => {
    if (!ev || !ev.key) return;
    if (ev.key === KEY_TOKENS.accesstoken || ev.key === KEY_TOKENS.accesstokenreports || ev.key === KEY_TOKENS.accesstoken2talk) applyStoredHeaders();
  });
  if (!lastActivity() && safeGet(KEY_TOKENS.accesstoken)) touchActivity({ type: 'init' });
  setInterval(checkRenewal, CHECK_EVERY_MS);
}

// Com gente na frente: renova perto de vencer OU por batimento (inatividade/3 desde a última renovação), para a inatividade no
// servidor acompanhar a interação real. Sem interação recente (inatividade/2): não renova; o token vence e a sessão cai no prazo.
function checkRenewal() {
  const token = safeGet(KEY_TOKENS.accesstoken);
  if (!token || !hasRefreshToken()) return;
  if (!recentlyActive()) return;
  const exp = tokenExpiry(token);
  const lastRefresh = Number(safeGet(KEY_LAST_REFRESH)) || 0;
  const nearExpiry = exp ? exp - Date.now() <= renewBeforeMs(lastRefresh ? exp - lastRefresh : 0) : false;
  const heartbeatDue = Date.now() - lastRefresh >= heartbeatMs();
  if (!nearExpiry && !heartbeatDue) return;
  refreshSession().catch((e) => { if (e && e.sessionReason && e.sessionReason !== 'session_invalid') endSession(e.sessionReason); });
}

const Session = { attachSession, storeTokens, clearSession, refreshSession, touchActivity, tokenExpiry, hasRefreshToken };
export default Session;
